import { FC, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Study } from '@/shared/api/protocol-ts/model/dto_study_pb';
import { useUserTimezone } from '@/shared/hooks';
import { openInNewBrowserTab } from '@/shared/lib';
import { Button, Icon, Popover, Spinner } from '@/shared/ui';

import { i18n } from '@/entities/study';

import { getReportStudyDownloads } from '../../lib';

import styles from './DetailsPopover.module.scss';

type DetailsPopoverProps = {
  reportID: string;
  sourceStudyIDs: string[];
  studies: Study[];
  isReportGenerationCompleted: boolean;
  isLoadingPDF: boolean;
  canArchive?: boolean;
  canDelete: boolean;
  canRestore?: boolean;
  hideMode?: boolean;
  isHidden?: boolean;
  shouldHideDownload?: boolean;
  className?: string;
  testID?: string;
  onDetailsView: () => void;
  onRemoveReport: () => void;
  onDownloadReport: () => Promise<void>;
  onHideReport?: () => void;
  onRestoreReport?: () => void;
};

export const DetailsPopover: FC<DetailsPopoverProps> = (props) => {
  const {
    reportID,
    sourceStudyIDs,
    studies,
    isReportGenerationCompleted,
    isLoadingPDF,
    className,
    testID,
    canArchive,
    canDelete,
    canRestore,
    hideMode,
    isHidden,
    shouldHideDownload,
    onDetailsView,
    onRemoveReport,
    onDownloadReport,
    onHideReport,
    onRestoreReport,
  } = props;

  const { formatMessage } = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const timezone = useUserTimezone();
  const studyDownloads = getReportStudyDownloads({
    reportID,
    sourceStudyIDs,
    studies,
    timezone,
  });

  const handleDetailsView = (event: React.MouseEvent) => {
    event.stopPropagation();
    onDetailsView();
    setIsOpen(false);
  };

  const handleDownloadPDF = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!isLoadingPDF) {
      await onDownloadReport();
      setIsOpen(false);
    }
  };

  const handleRemoveReport = (event: React.MouseEvent) => {
    event.stopPropagation();
    onRemoveReport();
    setIsOpen(false);
  };

  const handleHideReport = (event: React.MouseEvent) => {
    event.stopPropagation();
    onHideReport?.();
    setIsOpen(false);
  };

  const handleRestoreReport = (event: React.MouseEvent) => {
    event.stopPropagation();
    onRestoreReport?.();
    setIsOpen(false);
  };

  const removeReportButton = (
    <Button
      onClick={handleRemoveReport}
      variant="tertiary"
      size="small"
      danger
      icon={'delete'}
      className={styles.popoverButton}
    >
      <FormattedMessage id="reportCard.remove" defaultMessage="Remove" />
    </Button>
  );

  if (isHidden) {
    if (!canRestore && !canDelete) {
      return null;
    }

    return (
      <Popover
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        className={className}
        testID={testID}
        popoverClassName={styles.popover}
        iconClassName={styles.detailsIcon}
      >
        {canRestore && (
          <Button
            onClick={handleRestoreReport}
            variant="tertiary"
            size="small"
            icon={'back'}
            className={styles.popoverButton}
          >
            <FormattedMessage
              id="reportCard.restore"
              defaultMessage="Restore"
            />
          </Button>
        )}

        {canDelete && removeReportButton}
      </Popover>
    );
  }

  return (
    <Popover
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      className={className}
      testID={testID}
      popoverClassName={styles.popover}
      iconClassName={styles.detailsIcon}
    >
      <Button
        onClick={handleDetailsView}
        variant="tertiary"
        size="small"
        icon={'info'}
        className={styles.popoverButton}
      >
        <FormattedMessage
          id="reportCard.information"
          defaultMessage="Information"
        />
      </Button>

      {studyDownloads.map(({ studyType, href, disabled }) => (
        <Button
          key={studyType}
          disabled={disabled}
          onClick={(event) => {
            event.stopPropagation();
            openInNewBrowserTab(href);
            setIsOpen(false);
          }}
          variant="tertiary"
          size="small"
          icon="download"
          className={styles.popoverButton}
        >
          <FormattedMessage
            id="reportCard.downloadStudy"
            defaultMessage="Download {studyType}"
            values={{ studyType: formatMessage(i18n.studyTypes[studyType]) }}
          />
        </Button>
      ))}

      {isReportGenerationCompleted && !shouldHideDownload && (
        <Button
          onClick={handleDownloadPDF}
          variant="tertiary"
          size="small"
          className={styles.popoverButton}
        >
          {isLoadingPDF ? (
            <Spinner size={18} className={styles.downloadSpinner} />
          ) : (
            <Icon name="file" size={24} />
          )}
          <FormattedMessage
            id="global.reportPreview.settings.downloadPDF"
            defaultMessage="Download PDF"
          />
        </Button>
      )}

      {hideMode
        ? canArchive && (
            <Button
              onClick={handleHideReport}
              variant="tertiary"
              size="small"
              icon="archive"
              danger
              className={styles.popoverButton}
            >
              <FormattedMessage id="global.archive" defaultMessage="Archive" />
            </Button>
          )
        : canDelete && removeReportButton}
    </Popover>
  );
};
